import { devicesIconManifest } from 'app/agriculture/icons/devices/devicesIconManifest';
import { useSelection } from 'app/agriculture/ContextMenu/hooks/useSelection/useSelection';
import {
    createBillboardIconDataUri,
    createBillboardWithoutIconDataUri,
} from 'app/agriculture/icons/utils/map-icon-utils/createBillboardIconDataUri';
import { CesiumBillboard } from 'app/agriculture/FarmMain/MapRenderer/components/CesiumBillboard';
import { createIcon } from 'app/agriculture/hooks/useMapRender/mapRenderUtils';
import { primaryColourMain } from 'app/agriculture/material';
import {
    DeviceStatusContext,
    DeviceStatusProvider,
} from 'app/agriculture/routes/Devices/utils/status/DeviceStatusContext';
import { useContext, useMemo } from 'react';
import { statusToColour } from 'app/agriculture/routes/Devices/utils/status/statusToColour';
import { useTanks } from 'app/agriculture/queries/tanks/useTanks';
import { useDevicesByType } from 'app/agriculture/queries/devices/useDevicesByType';
import { Device } from 'app/agriculture/queries/devices/types/Device';
import { getAssetIconConfig } from 'app/agriculture/icons/utils/map-icon-utils/getAssetIconConfig';
import { Tank } from 'app/agriculture/queries/tanks/types/Tank';
import { useSnapshot } from 'app/agriculture/queries/snapshots/useSnapshot';
import { validateLevelSnapshot } from 'app/agriculture/routes/Devices/utils/validateLevelSnapshot';
import { brandConfig } from 'app/agriculture/queries/snapshots/config/brandConfig';

export function TankRenderer() {
    const tanks = useTanks();
    const tankSensors = useDevicesByType('Level Monitor');

    const tankSensorsByInfrastructureId = useMemo(() => {
        const map = new Map<string, Device>();
        for (const [, tankSensor] of tankSensors) {
            const { infrastructureId } = tankSensor;
            if (infrastructureId != null) {
                map.set(infrastructureId, tankSensor);
            }
        }
        return map;
    }, [tankSensors]);

    return (
        <>
            {Array.from(tanks.values()).map((tank) => {
                const sensorForThisTank = tankSensorsByInfrastructureId.get(tank.id);

                if (sensorForThisTank == null) {
                    return <TankMapEntity {...tank} key={tank.id} />;
                }

                return (
                    <DeviceStatusProvider
                        deviceId={sensorForThisTank.id}
                        key={sensorForThisTank.id}
                    >
                        <TankSensorMapEntity
                            {...sensorForThisTank}
                            locationOverride={{
                                latitude: tank.geometry.coordinates[1],
                                longitude: tank.geometry.coordinates[0],
                            }}
                        />
                    </DeviceStatusProvider>
                );
            })}
        </>
    );
}

function TankMapEntity({ id, geometry }: Tank) {
    const { isSelected } = useSelection();
    const { icon, options } = getAssetIconConfig('Tank');
    const image = !icon
        ? createBillboardWithoutIconDataUri(options)
        : createBillboardIconDataUri(icon, options);
    return (
        <CesiumBillboard
            key={id}
            billboardOptions={createIcon({
                id,
                image,
                coordinates: geometry.coordinates,
                isPin: options?.billboardType === 'pin' || false,
                isSelected: isSelected(id),
            })}
        />
    );
}

function TankSensorMapEntity(device: Device) {
    const { id, locationOverride } = device;
    const { isSelected } = useSelection();
    const { status } = useContext(DeviceStatusContext);
    const levelSnapshot = useSnapshot({
        parentId: id,
        snapshotType: brandConfig[device.brand].levelSnapshotType,
        shelfLife: brandConfig[device.brand].snapshotShelfLife,
    });
    const validatedLevelSnapshot = validateLevelSnapshot(levelSnapshot, { device });

    if (!locationOverride || device.infrastructureId == null) return null;

    return (
        <CesiumBillboard
            billboardOptions={createIcon({
                id: device.infrastructureId,
                coordinates: [locationOverride.longitude, locationOverride.latitude],
                isSelected: isSelected(device.infrastructureId) || isSelected(device.id),
                image: createBillboardIconDataUri(devicesIconManifest['Level Monitor'], {
                    color: 'white',
                    iconColor: primaryColourMain,
                    shouldIncludeStatus: true,
                    statusColor: statusToColour[status],
                    percentage: validatedLevelSnapshot?.percentageRemaining,
                }),
                isPin: false,
            })}
        />
    );
}
